import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import useCheckboxes from "../../Hooks/useCheckboxes";
import usePagedSortedTable, { TotalPageMappers, } from "../../Hooks/usePagedSortedTable";
import CourseService from "../../../Services/Course.service";
import BooleanDisplay from "../../UI/BooleanDisplay/BooleanDisplay";
import { SearchButton } from "../../UI/StandardButtons/StandardButtons";
import NumberService from "../../../Services/Shared/Number.service";
import useUniqueId from "../../Hooks/useUniqueId";
import { AlertError } from "../../UI/StandardAlerts/StandardAlerts";
const CREDITS_MIN = 0;
const CREDITS_MAX = 100;
const PAGE_SIZE = 20;
const SEARCH_DEFAULT = "";
const ACTIVE_DEFAULT = 1;
const HAS_ACTIVE_COURSE_SECTIONS_DEFAULT = 1;
const CREDITS_MIN_DEFAULT = null;
const CREDITS_MAX_DEFAULT = null;
const DEFAULT_SEARCH = {
    active: ACTIVE_DEFAULT,
    query: SEARCH_DEFAULT,
    hasActiveCourseSections: HAS_ACTIVE_COURSE_SECTIONS_DEFAULT,
    creditsMoreThan: CREDITS_MIN_DEFAULT,
    creditsLessThan: CREDITS_MAX_DEFAULT,
};
const CourseLookup = ({ selected: selectedCourses, onApply, children, }) => {
    var _a;
    const [query, setQuery] = React.useState(SEARCH_DEFAULT);
    const [active, setActive] = React.useState(ACTIVE_DEFAULT);
    const [hasActiveCourseSections, setHasActiveCourseSections] = React.useState(HAS_ACTIVE_COURSE_SECTIONS_DEFAULT);
    const [creditsMoreThan, setCreditsMoreThan] = React.useState(CREDITS_MIN_DEFAULT);
    const [creditsLessThan, setCreditsLessThan] = React.useState(CREDITS_MAX_DEFAULT);
    const [searchArgs, setSearchArgs] = React.useState(DEFAULT_SEARCH);
    const [validationError, setValidationError] = React.useState(null);
    const makeId = useUniqueId()[1];
    const searchItems = () => {
        if (creditsMoreThan != null &&
            creditsLessThan != null &&
            creditsMoreThan > creditsLessThan) {
            setValidationError("Credits Min cannot be greater than Credits Max");
        }
        else {
            setValidationError(null);
            setSearchArgs({
                query,
                active,
                hasActiveCourseSections,
                creditsLessThan,
                creditsMoreThan,
            });
        }
    };
    const { getCheckbox, checkedItems } = useCheckboxes({
        externallyCheckedItems: selectedCourses || [],
        // eslint-disable-next-line eqeqeq
        equalsFunc: (a, b) => a.id == b.id,
    });
    const { table, dataState } = usePagedSortedTable({
        addCheckboxes: {
            useCheckboxesMakeCheckbox: getCheckbox,
            getCheckedItem: (data, index) => data.data[index],
        },
        getData: (sort, page, sArgs) => {
            let isActive;
            switch (sArgs.active) {
                case 0:
                    isActive = false;
                    break;
                case 1:
                    isActive = true;
                    break;
                case 2:
                default:
                    isActive = null;
            }
            let activeCourseSections;
            switch (sArgs.hasActiveCourseSections) {
                case 0:
                    activeCourseSections = false;
                    break;
                case 1:
                    activeCourseSections = true;
                    break;
                case 2:
                default:
                    activeCourseSections = null;
            }
            return CourseService.search({
                query: sArgs.query,
                active: isActive,
                hasActiveCourseSections: activeCourseSections,
                creditsMax: sArgs.creditsLessThan == null ? null : sArgs.creditsLessThan + 1,
                creditsMin: sArgs.creditsMoreThan == null ? null : sArgs.creditsMoreThan - 1,
            }, {
                sort: sort === null || sort === void 0 ? void 0 : sort.sort,
                order: sort === null || sort === void 0 ? void 0 : sort.order,
                max: PAGE_SIZE,
                offset: page * PAGE_SIZE,
            });
        },
        searchArgs,
        getNumPages: (result) => TotalPageMappers.EngagePagedResult(result, PAGE_SIZE),
        noDataComponent: "No Courses found",
        initialSort: { sort: "name", order: "asc" },
        columns: [
            {
                title: "Code",
                sortId: "code",
                size: 2,
            },
            {
                title: "Name",
                sortId: "name",
            },
            { title: "Active", size: 2, sortId: "active", align: "center" },
            {
                title: "Current Sections",
                size: 2,
                sortId: "hasActiveCourseSections",
                align: "center",
            },
            { title: "Credits", sortId: "credits", align: "end", size: 2 },
        ],
        rows: ({ data }) => data.map((course) => [
            course.code,
            course.name,
            _jsx(BooleanDisplay, { val: course.active }, "active"),
            _jsx(BooleanDisplay, { val: course.hasActiveCourseSections || false }, "hasActiveCourseSections"),
            course.credits,
        ]),
    });
    return children({
        selectedItems: checkedItems,
        isLoading: dataState.isLoading,
        totalNumItems: (_a = dataState.data) === null || _a === void 0 ? void 0 : _a.total,
        lookup: (_jsxs(_Fragment, { children: [validationError && _jsx(AlertError, { message: validationError }), _jsxs("div", { className: "mb-3 form-inline", children: [_jsxs("div", { className: "form-group mr-3 mt-2 ", children: [_jsx("label", { htmlFor: "programSearch", className: "sr-only", children: "Search" }), _jsx("input", { type: "text", className: "form-control", id: "programSearch", placeholder: "Search Name, Code", style: { width: 200 }, value: query, onChange: (event) => {
                                        setQuery(event.target.value);
                                    }, onKeyPress: (e) => {
                                        if (e.key === "Enter") {
                                            searchItems();
                                        }
                                    } })] }), _jsxs("div", { className: "form-group mr-3 mt-2", children: [_jsxs("div", { className: "custom-control custom-radio custom-control-inline", children: [_jsx("input", { className: "custom-control-input", type: "radio", id: "activeTrue", value: "1", checked: active === 1, onChange: () => {
                                                setActive(1);
                                            } }), _jsx("label", { className: "custom-control-label", htmlFor: "activeTrue", children: "Active" })] }), _jsxs("div", { className: "custom-control custom-radio custom-control-inline", children: [_jsx("input", { className: "custom-control-input ", type: "radio", id: "activeFalse", checked: active === 0, onChange: () => {
                                                setActive(0);
                                            } }), _jsx("label", { className: "custom-control-label", htmlFor: "activeFalse", children: "Inactive" })] }), _jsxs("div", { className: "custom-control custom-radio custom-control-inline", children: [_jsx("input", { className: "custom-control-input", type: "radio", id: "activeBoth", checked: active === 2, onChange: () => {
                                                setActive(2);
                                            } }), _jsx("label", { className: "custom-control-label", htmlFor: "activeBoth", children: "Both" })] })] }), _jsxs("div", { className: "form-group mr-3 mt-2", children: [_jsxs("div", { className: "custom-control custom-radio custom-control-inline", children: [_jsx("input", { className: "custom-control-input", type: "radio", id: "courseSectionsTrue", value: "1", checked: hasActiveCourseSections === 1, onChange: () => {
                                                setHasActiveCourseSections(1);
                                            } }), _jsx("label", { className: "custom-control-label", htmlFor: "courseSectionsTrue", children: "Current Sections" })] }), _jsxs("div", { className: "custom-control custom-radio custom-control-inline", children: [_jsx("input", { className: "custom-control-input ", type: "radio", id: "courseSectionsFalse", checked: hasActiveCourseSections === 0, onChange: () => {
                                                setHasActiveCourseSections(0);
                                            } }), _jsx("label", { className: "custom-control-label", htmlFor: "courseSectionsFalse", children: "No Current Sections" })] }), _jsxs("div", { className: "custom-control custom-radio custom-control-inline", children: [_jsx("input", { className: "custom-control-input", type: "radio", id: "courseSectionsBoth", checked: hasActiveCourseSections === 2, onChange: () => {
                                                setHasActiveCourseSections(2);
                                            } }), _jsx("label", { className: "custom-control-label", htmlFor: "courseSectionsBoth", children: "Both" })] })] }), _jsxs("div", { className: "form-group mt-2 mr-3", children: [_jsx("label", { className: "sr-only", htmlFor: makeId("credits"), children: "Credits" }), _jsxs("div", { className: "input-group", style: { width: 250 }, children: [_jsx("input", { "aria-label": "Credits Min", id: makeId("creditsMoreThan"), type: "number", step: "1", min: CREDITS_MIN, max: CREDITS_MAX - 1, onChange: (e) => {
                                                console.log(e.target.value);
                                                if (!e.target.value) {
                                                    setCreditsMoreThan(null);
                                                }
                                                const number = parseInt(e.target.value, 10);
                                                if (NumberService.isValid(number)) {
                                                    setCreditsMoreThan(number);
                                                }
                                            }, value: creditsMoreThan !== null && creditsMoreThan !== void 0 ? creditsMoreThan : "", name: "creditsMoreThan", className: "form-control", placeholder: "Credits Min" }), _jsx("input", { id: makeId("creditsLessThan"), type: "number", step: "1", onChange: (e) => {
                                                if (!e.target.value) {
                                                    setCreditsLessThan(null);
                                                }
                                                const number = parseInt(e.target.value, 10);
                                                if (NumberService.isValid(number)) {
                                                    setCreditsLessThan(number);
                                                }
                                            }, value: creditsLessThan !== null && creditsLessThan !== void 0 ? creditsLessThan : "", min: CREDITS_MIN + 1, max: CREDITS_MAX, className: "form-control", placeholder: "Credits Max", name: "creditsLessThan" })] })] }), _jsx("div", { className: "mt-2", children: _jsx(SearchButton, { isLoading: dataState.isLoading, onClick: () => {
                                    searchItems();
                                } }) })] }), _jsx("hr", {}), table] })),
    });
};
export default CourseLookup;
